"use client";

import React, { useState, useEffect } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useTenancy } from "@/contexts/TenancyContext";
import { 
  updateProcess, 
  getProcess,
  Process, 
  ProcessType,
  getProcessTypeLabel 
} from "@/services/processService";
import { toast } from "sonner";
import { Loader2, FileText, ExternalLink, CheckCircle2 } from "lucide-react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faXmark, 
  faBarsProgress, 
  faMicrophone, 
  faFileLines, 
  faFileCircleQuestion,
  faClipboardCheck
} from "@awesome.me/kit-269fbef2e2/icons/duotone/solid";
import { cn } from "@/lib/utils";
import { DatePicker } from "@/components/ui/date-picker";
import { SingleUserCombobox } from "@/components/ui/SingleUserCombobox";
import { Badge } from "@/components/ui/badge";
import type { ComplianceEvent } from "@/types/compliance";

interface ReviewProcessDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedEvent?: ComplianceEvent | null;
  onDataChanged?: () => void;
}

const processTypeInfo: Record<ProcessType, { label: string; icon: any; color: string }> = {
  'documented': {
    label: 'Documented',
    icon: faFileLines,
    color: 'emerald',
  },
  'verbal': {
    label: 'Verbal',
    icon: faMicrophone,
    color: 'blue',
  },
  'undocumented': {
    label: 'Undocumented',
    icon: faFileCircleQuestion,
    color: 'amber',
  },
};

export function ReviewProcessDialog({ open, onOpenChange, selectedEvent, onDataChanged }: ReviewProcessDialogProps) {
  const { activeTenancy } = useTenancy();
  const companyID = activeTenancy?.id || (window as any).companyID;
  
  // Process data
  const [process, setProcess] = useState<Process | null>(null);
  const [loading, setLoading] = useState(false);
  
  // Form state - only the review-relevant fields
  const [reviewedBy, setReviewedBy] = useState("");
  const [reviewDate, setReviewDate] = useState<Date | null>(new Date());
  const [nextReviewDate, setNextReviewDate] = useState<Date | null>(null);
  const [comments, setComments] = useState("");
  const [saving, setSaving] = useState(false);
  
  // Calculate next review date based on repeat schedule
  const calculateNextReviewDate = (fromDate: Date, repeat?: Process['repeat']): Date => {
    const next = new Date(fromDate);
    
    if (!repeat || repeat.frequency === 'never') {
      // Default to 12 months if no repeat schedule
      next.setMonth(next.getMonth() + 12);
      return next;
    }
    
    const interval = repeat.interval || 1;
    
    switch (repeat.frequency) {
      case 'daily':
        next.setDate(next.getDate() + interval);
        break;
      case 'weekly':
        next.setDate(next.getDate() + (7 * interval));
        break;
      case 'monthly':
        next.setMonth(next.getMonth() + interval);
        break;
      case 'quarterly':
        next.setMonth(next.getMonth() + (3 * interval));
        break;
      case 'annually':
        next.setFullYear(next.getFullYear() + interval);
        break;
      default:
        next.setMonth(next.getMonth() + 12);
    }
    
    return next;
  };
  
  // Load process data when dialog opens
  useEffect(() => {
    if (open && selectedEvent?.processId && companyID) {
      setLoading(true);
      getProcess(companyID, selectedEvent.processId)
        .then((p) => {
          if (p) {
            setProcess(p);
            setReviewedBy(p.owner || "");
            // Calculate next review date based on repeat schedule
            const today = new Date();
            setNextReviewDate(calculateNextReviewDate(today, p.repeat));
          }
        })
        .catch((err) => {
          console.error('[ReviewProcessDialog] Failed to load process:', err);
          toast.error('Failed to load process');
        })
        .finally(() => setLoading(false));
    }
  }, [open, selectedEvent?.processId, companyID]);
  
  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setProcess(null);
      setReviewedBy("");
      setReviewDate(new Date());
      setNextReviewDate(null);
      setComments("");
    }
  }, [open]);
  
  // Set current user as reviewer by default
  useEffect(() => {
    if (open && (window as any).currentUser?.userID) {
      setReviewedBy((window as any).currentUser.userID);
    }
  }, [open]);

  const handleCompleteReview = async () => {
    if (!process || !companyID) return;
    
    setSaving(true);
    try {
      await updateProcess(companyID, process.id, {
        reviewDate: reviewDate || undefined,
        nextReviewDate: nextReviewDate || undefined,
        lastReviewDate: reviewDate || undefined,
        // Store review comment in metadata
        metadata: {
          ...process.metadata,
          lastReviewComment: comments,
          lastReviewedBy: reviewedBy,
          lastReviewedAt: new Date(),
        }
      });
      
      toast.success('Process review completed');
      onDataChanged?.();
      onOpenChange(false);
    } catch (err) {
      console.error('[ReviewProcessDialog] Failed to save review:', err);
      toast.error('Failed to save review');
    } finally {
      setSaving(false);
    }
  };

  const typeInfo = process?.type ? processTypeInfo[process.type] : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="max-w-4xl! w-full max-h-[90vh] overflow-hidden rounded-2xl border border-border bg-card sm:p-0! shadow-2xl [&>button]:hidden p-0"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="flex flex-col h-full max-h-[90vh] p-0">
          {/* Close button */}
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            className="absolute right-3 top-3 z-50 h-8 w-8 rounded-lg bg-muted hover:bg-muted/80 flex items-center justify-center transition-colors disabled:opacity-50"
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faXmark} className="h-4 w-4 text-muted-foreground" />
          </button>
          
          {/* Header */}
          <div className="px-6 py-5 border-b border-border bg-card text-center shrink-0">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-linear-to-br from-emerald-500/10 to-emerald-500/30 mb-4">
              <FontAwesomeIcon icon={faClipboardCheck} className="w-8 h-8 text-emerald-500" />
            </div>
            <h2 className="text-3xl font-bold text-foreground">Review Process</h2>
            <p className="mt-3 text-muted-foreground max-w-md mx-auto">
              Complete the review for this process and set the next review date.
            </p>
            {typeInfo && (
              <Badge 
                variant="outline" 
                className={cn(
                  "mt-3",
                  typeInfo.color === 'emerald' && "border-emerald-500/30 text-emerald-600",
                  typeInfo.color === 'blue' && "border-blue-500/30 text-blue-600",
                  typeInfo.color === 'amber' && "border-amber-500/30 text-amber-600"
                )}
              >
                <FontAwesomeIcon icon={typeInfo.icon} className="w-3 h-3 mr-1" />
                {typeInfo.label} Process
              </Badge>
            )}
          </div>

          {/* Content */}
          <div className="px-6 py-5 flex-1 min-h-0 overflow-y-auto space-y-4">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : process ? (
              <>
                {/* Process Info (read-only) */}
                <div className="rounded-lg border border-border bg-muted/30 p-4">
                  <div className="flex items-start gap-4">
                    <div className="shrink-0 w-12 h-12 rounded-lg bg-linear-to-br from-primary/10 to-primary/30 flex items-center justify-center">
                      <FontAwesomeIcon icon={faBarsProgress} className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <Label className="block text-sm font-semibold text-foreground mb-1">
                        Process Name
                      </Label>
                      <p className="text-base text-foreground">{process.name}</p>
                      {process.description && (
                        <p className="text-sm text-muted-foreground mt-2">{process.description}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Linked Document (for documented processes) */}
                {process.type === 'documented' && (process.documentLibraryRef || process.documentId) && (
                  <div className="rounded-lg border border-emerald-500/30 bg-emerald-50 dark:bg-emerald-500/10 p-4">
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-emerald-500 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">
                          {process.documentLibraryRef?.fileName || process.name || 'Linked Document'}
                        </p>
                        {process.documentLibraryRef?.documentComponentId && (
                          <p className="text-xs text-muted-foreground">
                            {process.documentLibraryRef.documentComponentId}
                            {process.documentLibraryRef.documentVersion && ` • v${process.documentLibraryRef.documentVersion}`}
                          </p>
                        )}
                      </div>
                      {process.documentLibraryRef?.fileUrl && (
                        <a
                          href={process.documentLibraryRef.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="shrink-0 p-2 rounded-lg bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-500 transition-colors"
                          title="Open document"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  </div>
                )}

                {/* Review Fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Reviewed By */}
                  <div className="rounded-lg border border-border bg-muted/30 p-4">
                    <Label className="block text-sm font-semibold text-foreground mb-3">
                      Reviewed By
                    </Label>
                    <SingleUserCombobox
                      selected={reviewedBy}
                      setSelected={setReviewedBy}
                      placeholder="Select reviewer..."
                      className="bg-background border-border h-11"
                    />
                  </div>

                  {/* Date Reviewed */}
                  <div className="rounded-lg border border-border bg-muted/30 p-4">
                    <Label className="block text-sm font-semibold text-foreground mb-3">
                      Date Reviewed
                    </Label>
                    <DatePicker 
                      date={reviewDate || undefined} 
                      setDate={(d) => setReviewDate(d || null)} 
                      fullWidth 
                      enableNaturalInput 
                      showPreview 
                      placeholder="Today" 
                    />
                  </div>
                </div>

                {/* Next Review Date */}
                <div className="rounded-lg border border-border bg-muted/30 p-4">
                  <div className="flex items-center justify-between mb-1">
                    <Label className="block text-sm font-semibold text-foreground">
                      Next Review Date
                    </Label>
                    {/* {process?.repeat && process.repeat.frequency !== 'never' && (
                      <span className="text-xs text-muted-foreground">
                        Based on: {process.repeat.interval > 1 ? `Every ${process.repeat.interval} ` : ''}{process.repeat.frequency}
                      </span>
                    )} */}
                  </div>
                  <p className="text-xs text-muted-foreground mb-3">
                    {process?.repeat && process.repeat.frequency !== 'never' 
                      ? 'Auto-calculated from repeat schedule (can be adjusted)'
                      : 'Defaults to 12 months from today'}
                  </p>
                  <DatePicker 
                    date={nextReviewDate || undefined} 
                    setDate={(d) => setNextReviewDate(d || null)} 
                    fullWidth 
                    enableNaturalInput 
                    showPreview 
                    placeholder="Select next review date" 
                  />
                </div>

                {/* Comments */}
                <div className="rounded-lg border border-border bg-muted/30 p-4">
                  <Label className="block text-sm font-semibold text-foreground mb-1">
                    Review Comments
                  </Label>
                  <p className="text-xs text-muted-foreground mb-3">
                    Add any notes about this review
                  </p>
                  <Textarea
                    placeholder="Add notes about the process review..."
                    value={comments}
                    onChange={(e) => setComments(e.target.value)}
                    className="bg-background border border-border min-h-[100px] focus:border-primary"
                  />
                </div>
              </>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                Process not found
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-border bg-muted/30 shrink-0">
            <div className="flex items-center justify-between gap-4">
              <div className="text-xs text-muted-foreground truncate">
                {process?.componentId && `ID: ${process.componentId}`}
              </div>
              <div className="flex gap-2 shrink-0">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => onOpenChange(false)}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleCompleteReview}
                  disabled={saving || !process}
                  className="bg-primary hover:bg-primary/80 text-white"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Complete Review
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ReviewProcessDialog;
